import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
// motion not used in this component

const HeaderContainer = styled.header`
  background: #1a1a1a;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const TopBar = styled.div`
  background: #ff6600;
  color: white;
  padding: 10px 0;
  font-size: 14px;

  .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .contact-info {
    display: flex;
    align-items: center;
    gap: 2rem;

    a {
      color: white;
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &:hover {
        color: #fff2e6;
      }
    }
  }

  @media (max-width: 768px) {
    .contact-info {
      gap: 1rem;
      font-size: 12px;
    }
  }
`;

const MainHeader = styled.div`
  padding: 0.5rem 0;
  background: #1a1a1a;

  .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }

  .logo {
    img {
      height: 120px;
      width: auto;
    }
  }

  /* Navigation moved into MainHeader so logo and menu live in one row */
  .nav-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    position: relative;
    flex: 1;
    gap: 1rem;
  }

  .nav-menu {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      position: relative;

      &:hover .dropdown {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }
    }

    a {
      display: block;
      padding: 1rem 1rem;
      color: #e0e0e0;
      font-weight: 500;
      transition: all 0.3s ease;

      &:hover,
      &.active {
        color: #ff6600;
        background: rgba(255, 102, 0, 0.06);
      }
    }
  }

  .dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    background: #1a1a1a;
    min-width: 250px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
    border: 1px solid #333;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px);
    transition: all 0.3s ease;
    z-index: 1000;

    a {
      padding: 0.75rem 1.5rem;
      border-bottom: 1px solid #333;
      color: #e0e0e0;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background: #111;
        color: #ff6600;
      }
    }
  }

  .mobile-menu-button {
    display: none;
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #e0e0e0;
    cursor: pointer;
    padding: 1rem;

    @media (max-width: 768px) {
      display: block;
    }
  }

  .mobile-menu {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #1a1a1a;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
    border-top: 1px solid #333;
    z-index: 1000;

    &.open {
      display: block;
    }

    .nav-menu {
      flex-direction: column;

      li {
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
          border-bottom: none;
        }
      }

      a {
        padding: 1rem;
      }

      .dropdown {
        position: static;
        opacity: 1;
        visibility: visible;
        transform: none;
        box-shadow: none;
        background: #111;
        border: none;

        a {
          padding-left: 2rem;
        }
      }
    }
  }

  @media (max-width: 768px) {
    .nav-menu {
      display: none;
    }
  }
`;

/* Navigation styled component removed - styles consolidated in MainHeader */

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <HeaderContainer>
      <TopBar>
        <div className="container">
          <div className="contact-info">
            <a href="tel:0481313403">
              <img
                src="/images/phn_icon_whte.png"
                alt="Phone"
              />
              0481 313 403
            </a>
            <a href="mailto:contact@roadwisetyres.com.au">
              <img
                src="/images/email_icon.png"
                alt="Email"
              />
              contact@roadwisetyres.com.au
            </a>
          </div>
        </div>
      </TopBar>

      <MainHeader>
        <div className="container">
          <div className="logo">
            <Link to="/">
              <img
                src="/images/logo.png"
                alt="Roadwise tyres"
              />
            </Link>
          </div>

          <div className="nav-container">
            <button
              className="mobile-menu-button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              ☰
            </button>

            <ul className="nav-menu">
              <li>
                <Link
                  to="/"
                  className={isActive("/") ? "active" : ""}>
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className={isActive("/about") ? "active" : ""}>
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className={isActive("/services") ? "active" : ""}>
                  Services
                </Link>
                <div className="dropdown">
                  <Link to="/services#mobile-fitting">Mobile Tyre Fitting</Link>
                  <Link to="/services#new-tyres">Reliable New Tyres</Link>
                  <Link to="/services#puncture-repair">Tyre Puncture Repair</Link>
                  <Link to="/services#tyre-rotation">Tyre Rotation</Link>
                </div>
              </li>
              <li>
                <Link
                  to="/landlux-tyres"
                  className={isActive("/landlux-tyres") ? "active" : ""}>
                  Tyres
                </Link>
                <div className="dropdown">
                  <Link to="/landlux-tyres">Landlux Tyres</Link>
                </div>
              </li>
              <li>
                <Link
                  to="/contact"
                  className={isActive("/contact") ? "active" : ""}>
                  Contact Us
                </Link>
              </li>
            </ul>

            <div className={mobileMenuOpen ? "mobile-menu open" : "mobile-menu"}>
              <ul className="nav-menu">
                <li>
                  <Link
                    to="/"
                    onClick={() => setMobileMenuOpen(false)}>
                    Home
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    onClick={() => setMobileMenuOpen(false)}>
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services"
                    onClick={() => setMobileMenuOpen(false)}>
                    Services
                  </Link>
                  <div className="dropdown">
                    <Link
                      to="/services#mobile-fitting"
                      onClick={() => setMobileMenuOpen(false)}>
                      Mobile Tyre Fitting
                    </Link>
                    <Link
                      to="/services#new-tyres"
                      onClick={() => setMobileMenuOpen(false)}>
                      Reliable New Tyres
                    </Link>
                    <Link
                      to="/services#puncture-repair"
                      onClick={() => setMobileMenuOpen(false)}>
                      Tyre Puncture Repair
                    </Link>
                    <Link
                      to="/services#tyre-rotation"
                      onClick={() => setMobileMenuOpen(false)}>
                      Tyre Rotation
                    </Link>
                  </div>
                </li>
                <li>
                  <Link
                    to="/landlux-tyres"
                    onClick={() => setMobileMenuOpen(false)}>
                    Tyres
                  </Link>
                  <div className="dropdown">
                    <Link
                      to="/landlux-tyres"
                      onClick={() => setMobileMenuOpen(false)}>
                      Landlux Tyres
                    </Link>
                  </div>
                </li>
                <li>
                  <Link
                    to="/contact"
                    onClick={() => setMobileMenuOpen(false)}>
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </MainHeader>
    </HeaderContainer>
  );
};

export default Header;
